import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { ProductImage } from '@app/components/product-image'
import { Card, CardContent, CardHeader, CardTitle } from '@app/components/ui/card'
import { cn } from '@app/lib/cn'
import { productionApi, productionKeys } from './api'
import { EMIR_LIST_ANCHOR } from './emir-list'
import { formatNumber, formatDateTime } from './format'
import { ErrorState, Skeleton, Stat } from './parts'
import type { BoardProses } from './production-api'

/**
 * Process board — the live view of the shop floor.
 *
 * Each proses is one column. The two bars underneath a column are queue
 * meters: waiting (mustard) and in-progress (steel) pairs, scaled against the
 * fullest proses. It exists so where the factory is jammed up reads at a
 * glance — you can see TEMİZLEME is many times fuller than KESİM without
 * reading a single number.
 *
 * A column only ever shows its top few emirs, and "12 emir daha bu proseste"
 * used to be the end of the road. Both the column heading and that line are now
 * links into the emir list below, filtered to the process (`?proses=04`), so
 * the board answers "where is the jam" and hands you the list of what is stuck.
 */
export function ProductionBoard() {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: productionKeys.board,
    queryFn: productionApi.board,
    refetchInterval: 60_000,
  })

  return (
    <Card>
      <CardHeader className="flex-wrap gap-y-2">
        <div>
          <CardTitle>Süreç panosu</CardTitle>
          <p className="mt-0.5 text-micro text-ink-muted">
            Prosesteki açık iş: kuyrukta bekleyen ve şu an işlenen çift adedi.
          </p>
        </div>
        {data ? (
          <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
            <Stat label="Bekleyen" value={formatNumber(data.total.waitingPairs)} unit="çift" tone="warn" />
            <Stat label="İşlemde" value={formatNumber(data.total.inProgressPairs)} unit="çift" tone="info" />
            {/* "Açık emir" read as "everything not closed" while the query counts
                only Durum='B' — the label now says exactly that scope, and the
                planned backlog (often larger) sits next to it instead of having
                no representation on the board at all. */}
            <Stat label="Üretimde" value={formatNumber(data.total.activeEmirCount)} unit="emir" />
            <PlannedStat count={data.total.plannedEmirCount} />
            <Stat
              label="Termini geçen"
              value={formatNumber(data.total.overdueEmirCount)}
              unit="emir"
              tone={data.total.overdueEmirCount > 0 ? 'accent' : 'neutral'}
            />
          </div>
        ) : null}
      </CardHeader>

      <CardContent className="p-0">
        {isError ? (
          <ErrorState message={error instanceof Error ? error.message : undefined} onRetry={refetch} />
        ) : isPending ? (
          <BoardSkeleton />
        ) : (
          <BoardColumns proses={data.proses} updatedAt={data.updatedAt} />
        )}
      </CardContent>
    </Card>
  )
}

/**
 * The planned backlog as a header stat that IS the door to it: the board's
 * open-work queries cannot see Durum='' emirs, so this number is the only
 * place the board admits they exist, and clicking it lands on the list
 * filtered to them. The chevron on the eyebrow is the same "this heading is a
 * way in" cue the proses column headings use. The proses filter AND the text
 * search are dropped on purpose: a planned emir has no open work at any
 * proses, and the stat promises the GLOBAL count — landing on a list narrowed
 * by a leftover search would contradict the number just clicked.
 *
 * The onClick scroll exists because the hash alone only scrolls when it is
 * NEW: once the URL already carries #emir-listesi (any earlier board click),
 * the router sees an unchanged hash and stays put — and the list's own
 * scroll effect ignores the proses-cleared case. Without this, the click
 * "did nothing" visibly, the exact complaint this screen was fixed for.
 */
function PlannedStat({ count }: { count: number }) {
  return (
    <Link
      from="/uretim"
      to="/uretim"
      search={(prev) => ({
        ...prev,
        status: 'planned',
        proses: undefined,
        query: undefined,
        page: undefined,
      })}
      hash={EMIR_LIST_ANCHOR}
      onClick={(event) => {
        // Modified clicks open a new tab — this page must not move then.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
          return
        document.getElementById(EMIR_LIST_ANCHOR)?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'start',
        })
      }}
      title="Henüz üretime verilmemiş emirler — listelemek için tıklayın"
      className="group min-w-0"
    >
      <p className="eyebrow flex items-center gap-0.5 truncate transition-colors group-hover:text-kordovan">
        Planlandı
        <ChevronRightIcon aria-hidden className="size-2.5 shrink-0" />
      </p>
      <p className="font-mono text-lg leading-6 font-medium text-hardal tnum">
        {formatNumber(count)}
        <span className="ml-1 text-micro text-ink-muted">emir</span>
      </p>
    </Link>
  )
}

function BoardColumns({
  proses,
  updatedAt,
}: {
  proses: readonly BoardProses[]
  updatedAt: string
}) {
  // The peak single value is used as the baseline so the bars compare on one scale.
  const peak = proses.reduce(
    (acc, p) => Math.max(acc, p.waitingPairs, p.inProgressPairs),
    0,
  )

  return (
    <>
      {/* Hairline dividers are drawn with gap-px + the background color; no border stacking. */}
      <div className="grid grid-cols-1 gap-px bg-hairline sm:grid-cols-2 lg:grid-cols-4">
        {proses.map((p) => (
          <ProsesColumn key={p.proses} proses={p} peak={peak} />
        ))}
      </div>
      <p className="border-t border-hairline px-3.5 py-1.5 text-micro text-ink-muted">
        Son yenileme {formatDateTime(updatedAt.slice(0, 19))} · dakikada bir kendiliğinden yenilenir
      </p>
    </>
  )
}

function ProsesColumn({ proses, peak }: { proses: BoardProses; peak: number }) {
  const open = proses.waitingPairs + proses.inProgressPairs
  const empty = open === 0

  return (
    <section className={cn('flex min-w-0 flex-col bg-surface', empty && 'opacity-55')}>
      <header className="px-3 pt-2.5">
        <ProsesLink proses={proses} className="group flex items-baseline gap-2">
          <span className="font-mono text-micro text-ink-muted tnum">{proses.proses}</span>
          <h3 className="truncate font-display text-data font-semibold tracking-tight text-ink group-hover:text-kordovan">
            {proses.prosesName}
          </h3>
          {/* The only cue that a heading is a way into the filtered emir list. */}
          <ChevronRightIcon
            aria-hidden
            className="ml-auto size-3 shrink-0 self-center text-ink-muted/45 transition-colors group-hover:text-kordovan"
          />
        </ProsesLink>
      </header>

      {/* Each number has its own queue meter right underneath it, for cross-proses comparison. */}
      <dl className="grid grid-cols-2 gap-x-3 border-b border-hairline px-3 pb-2.5 pt-1.5">
        <div>
          <dt className="eyebrow">Bekleyen</dt>
          <dd className="font-mono text-base leading-5 font-medium text-hardal tnum">
            {formatNumber(proses.waitingPairs)}
          </dd>
          <dd className="text-micro text-ink-muted">{formatNumber(proses.waitingEmirCount)} emir</dd>
          <dd className="mt-1.5">
            <QueueMeter value={proses.waitingPairs} peak={peak} color="bg-hardal" />
          </dd>
        </div>
        <div>
          <dt className="eyebrow">İşlemde</dt>
          <dd className="font-mono text-base leading-5 font-medium text-celik tnum">
            {formatNumber(proses.inProgressPairs)}
          </dd>
          <dd className="text-micro text-ink-muted">{formatNumber(proses.inProgressEmirCount)} emir</dd>
          <dd className="mt-1.5">
            <QueueMeter value={proses.inProgressPairs} peak={peak} color="bg-celik" />
          </dd>
        </div>
      </dl>

      <div className="flex min-h-16 flex-1 flex-col gap-px p-1.5">
        {proses.emirs.length === 0 ? (
          <p className="px-1.5 py-2 text-micro text-ink-muted">
            {empty ? 'Bu proseste açık iş yok.' : 'Emir bulunamadı.'}
          </p>
        ) : (
          proses.emirs.map((emir) => (
            <Link
              key={emir.emirNo}
              to="/uretim/$emirNo"
              params={{ emirNo: String(emir.emirNo) }}
              // Touched on tablets on the shop floor: row height never drops below 40px.
              className="flex min-h-10 items-center gap-2 rounded-xs px-1.5 py-1 transition-colors hover:bg-kordovan/6"
            >
              {/* The model photo — what a foreman scanning a column recognises
                  well before the model code. `line` rather than `thumb` so the
                  chip stays exactly on its 40px touch target. */}
              <ProductImage
                imageKey={emir.imageKey}
                alt={`${emir.model ?? emir.modelKod ?? 'Model'} fotoğrafı`}
                size="line"
              />
              <span className="font-mono text-data text-kordovan tnum">{emir.emirNo}</span>
              <span className="min-w-0 flex-1 truncate text-micro text-ink-muted">
                {emir.model ?? emir.modelKod ?? '—'}
              </span>
              {/* Hue alone (steel vs mustard) carried the waiting/in-progress
                  split on this bare number; the title spells it out. */}
              <span
                title={`${formatNumber(emir.waitingPairs)} bekleyen · ${formatNumber(emir.inProgressPairs)} işlemde`}
                className={cn(
                  'font-mono text-data tnum',
                  emir.inProgressPairs > 0 ? 'text-celik' : 'text-hardal',
                )}
              >
                {formatNumber(emir.waitingPairs + emir.inProgressPairs)}
              </span>
            </Link>
          ))
        )}

        {proses.otherEmirCount > 0 ? (
          <ProsesLink
            proses={proses}
            className="flex items-center gap-1 px-1.5 pt-1 text-micro text-ink-muted hover:text-kordovan"
          >
            {formatNumber(proses.otherEmirCount)} emir daha bu proseste
            <ChevronRightIcon aria-hidden className="size-3 shrink-0" />
          </ProsesLink>
        ) : null}
      </div>
    </section>
  )
}

/**
 * Link into the emir list, filtered to this process.
 *
 * The status filter is left at whatever the list is already showing — coming
 * from the board you almost always want its default ("devam eden"), and
 * carrying the rest of the search over means a board click doesn't silently
 * throw away a search somebody typed. The hash puts the list on screen; the
 * list re-scrolls itself when only the process changes.
 *
 * Known, accepted gap: text still sitting on the search box's 300 ms debounce
 * is NOT carried (`prev` only has the committed query) — the box visibly
 * resets instead. Reaching a board heading within 300 ms of typing in the
 * list card below the board requires implausible pointer travel, and carrying
 * it would need cross-component flush plumbing whose search-updater runs at
 * render time for the href. Revisit only if someone actually hits it.
 */
function ProsesLink({
  proses,
  className,
  children,
}: {
  proses: BoardProses
  className?: string
  children: ReactNode
}) {
  return (
    <Link
      from="/uretim"
      to="/uretim"
      search={(prev) => ({ ...prev, proses: proses.proses, page: undefined })}
      hash={EMIR_LIST_ANCHOR}
      aria-label={`${proses.prosesName} prosesindeki emirleri listele`}
      className={className}
    >
      {children}
    </Link>
  )
}

/**
 * A single bar. Square-root scale: on a linear scale TEMİZLEME (5,203 pairs)
 * completely dwarfed the other proses and a 165-pair queue collapsed into a
 * hairline. Square root keeps both "which proses is fullest" ranking and
 * small queues legible.
 */
function QueueMeter({ value, peak, color }: { value: number; peak: number; color: string }) {
  const ratio = peak > 0 && value > 0 ? Math.sqrt(value) / Math.sqrt(peak) : 0
  return (
    <span aria-hidden className="block h-1 w-full rounded-xs bg-kagit">
      <span
        className={cn('block h-full rounded-xs', color)}
        style={{ width: ratio > 0 ? `${Math.max(4, ratio * 100)}%` : 0 }}
      />
    </span>
  )
}

function BoardSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-px bg-hairline sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 8 }, (_, index) => (
        <div key={index} className="flex flex-col gap-2 bg-surface p-3">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </div>
  )
}
